<template>
  <v-container
    v-if="$can('view-finance-details', 'Yacht')"
    fluid
  >
    <v-row
      align="center"
      class="mb-6"
      no-gutters
    >
      <v-col>
        <div class="headline">
          Yachts Cash Flow Overview
        </div>
      </v-col>
      <v-col
        cols="auto"
        class="pr-6"
      >
        <v-tabs
          v-model="filters.direction"
          background-color="transparent"
          class="ma-0"
        >
          <v-tab
            key="inbound"
            class="text-none"
            href="#inbound"
          >
            Inbound
          </v-tab>
          <v-tab
            key="outbound"
            class="text-none"
            href="#outbound"
          >
            Outbound
          </v-tab>
        </v-tabs>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center">
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="year in years"
              :key="year"
              class="ma-1"
              :color="isYearSelected(year) ? 'primary' : ''"
              :outlined="!isYearSelected(year)"
              small
              @click="toggleYearFilter(year)"
            >
              {{ year }}
            </v-chip>
          </div>
        </div>
      </v-col>
      <v-col
        cols="auto"
        class="pl-3"
      >
        <v-switch
          v-model="filters.sold_only"
          label="Sold Yachts Only"
          hide-details
          class="mt-0 pt-0"
        />
      </v-col>
    </v-row>

    <v-row class="mb-6">
      <v-col>
        <stat-card
          color="#1976D2"
          class="clickable"
          :class="{ 'filtered': !!filters.type, 'selected': filters.type === 'actual' }"
          @click.native="toggleFilter('type', 'actual')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.actual | currency('€') }}
          </div>
          <div class="subtitle-1">
            Actual
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#1976D2"
          class="clickable"
          :class="{ 'filtered': !!filters.type, 'selected': filters.type === 'forecast' }"
          @click.native="toggleFilter('type', 'forecast')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.forecast | currency('€') }}
          </div>
          <div class="subtitle-1">
            Forecast
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#f57c00"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'expected' }"
          @click.native="toggleFilter('status', 'expected')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.expected | currency('€') }}
          </div>
          <div class="subtitle-1">
            Status Expected
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#f57c00"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'received' }"
          @click.native="toggleFilter('status', 'received')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.received | currency('€') }}
          </div>
          <div class="subtitle-1">
            Status Received
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#f57c00"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'overdue' }"
          @click.native="toggleFilter('status', 'overdue')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.overdue | currency('€') }}
          </div>
          <div class="subtitle-1">
            Status Overdue
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#f57c00"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'overpaid' }"
          @click.native="toggleFilter('status', 'overpaid')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.overpaid | currency('€') }}
          </div>
          <div class="subtitle-1">
            Status Overpaid
          </div>
        </stat-card>
      </v-col>
      <v-col>
        <stat-card
          color="#f57c00"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'paid' }"
          @click.native="toggleFilter('status', 'paid')"
        >
          <div class="headline font-weight-bold mb-1">
            {{ stats.data.paid | currency('€') }}
          </div>
          <div class="subtitle-1">
            Status Paid
          </div>
        </stat-card>
      </v-col>
    </v-row>

    <v-row class="mb-12">
      <v-col
        v-for="category in categoryData"
        :key="category.key"
      >
        <stat-card
          class="clickable"
          dense
          :class="{ 'filtered': !!filters.category, 'selected': filters.category === category.key }"
          @click.native="toggleFilter('category', category.key)"
        >
          <div class="text-body-1 font-weight-bold">
            {{ stats.data[category.key] | currency('€') }}
          </div>
          <div class="text-body-1">
            {{ category.label }}
          </div>
        </stat-card>
      </v-col>
    </v-row>

    <pagination
      :from="yachtPayments.meta.from"
      :to="yachtPayments.meta.to"
      :total="yachtPayments.meta.total"
      :last-page="yachtPayments.meta.last_page"
      :page.sync="pagination.page"
      class="mt-1 align-end"
      has-actions
    >
      <template v-slot:action>
        <v-btn
          rounded
          text
          class="caption text-none ma-0"
          color="grey darken-2"
          :loading="exportingToXlsx"
          @click="downloadXls()"
        >
          <v-icon
            left
            small
          >
            download
          </v-icon>
          Export to XLSX
        </v-btn>
      </template>
    </pagination>

    <v-data-table
      :headers="headers"
      :items="yachtPayments.data"
      :loading="yachtPayments.loading"
      hide-default-footer
      class="elevation-5"
      :page.sync="pagination.page"
      :sort-by.sync="sorting.sortBy"
      :sort-desc.sync="sorting.desc"
      :server-items-length="yachtPayments.meta.total"
      disable-pagination
    >
      <template v-slot:item="{ item: yachtPayment }">
        <tr>
          <td>
            {{ yachtPayment.yacht_id }}
          </td>
          <td>
            <div
              v-if="!yachtPayment.yacht.name"
              class="grey--text text--lighten-1"
            >
              <router-link :to="{ name: 'yachtFinanceDetails', params: { id: yachtPayment.yacht.id } }">
                #{{ yachtPayment.yacht.id }} No Name
              </router-link>
            </div>
            <div v-else>
              <router-link :to="{ name: 'yachtFinanceDetails', params: { id: yachtPayment.yacht.id } }">
                #{{ yachtPayment.yacht.id }} {{ yachtPayment.yacht.name }}
              </router-link>
            </div>
            <div
              v-if="!yachtPayment.yacht.model.full_label"
              class="grey--text text--lighten-1"
            >
              #{{ yachtPayment.yacht.model.id }}
            </div>
            <div
              v-else
              class="font-weight-light grey--text text--darken-2 text-truncate"
            >
              #{{ yachtPayment.yacht.model.id }} &middot; {{ yachtPayment.yacht.model.full_label }}
            </div>
          </td>
          <td>
            <div v-if="yachtPayment.yacht.owner && yachtPayment.yacht.owner.name">
              {{ yachtPayment.yacht.owner.name }}
            </div>
            <div
              v-else
              class="grey--text"
            >
              N/A
            </div>
          </td>
          <td>
            {{ yachtPayment.date | moment('D MMM YYYY') }}
          </td>
          <td>
            <v-chip
              :color="statusData[yachtPayment.status].color"
              dark
              label
              small
            >
              {{ statusData[yachtPayment.status].label }}
            </v-chip>
          </td>
          <td class="text-right text-no-wrap">
            <strong>{{ yachtPayment.currency }} {{ yachtPayment.amount | currency('') }}</strong>
          </td>
          <td>
            <v-chip
              v-if="yachtPayment.type === 'forecast'"
              label
              color="orange darken-2"
              small
              outlined
            >
              Forecast
            </v-chip>
            <v-chip
              v-else-if="yachtPayment.type === 'actual'"
              label
              color="blue darken-2"
              small
              outlined
            >
              Actual
            </v-chip>
          </td>
          <td>
            <strong>{{ categoryData[yachtPayment.category].label }}</strong>
          </td>
          <td>
            <div v-if="yachtPayment.yacht.hull_number">
              {{ yachtPayment.yacht.hull_number }}
            </div>
            <div
              v-else
              class="grey--text"
            >
              N/A
            </div>
          </td>
          <td>
            <div v-if="yachtPayment.yacht.delivery_year">
              {{ yachtPayment.yacht.delivery_year }}
            </div>
            <div
              v-else
              class="grey--text"
            >
              N/A
            </div>
          </td>
          <td>
            <div v-if="yachtPayment.yacht.ex_factory_date">
              {{ yachtPayment.yacht.ex_factory_date | moment('D MMM YYYY') }}
            </div>
            <div
              v-else
              class="grey--text"
            >
              N/A
            </div>
          </td>
          <td>
            <div v-if="yachtPayment.yacht.finance_model">
              {{ yachtPayment.yacht.finance_model.label }}
            </div>
            <div
              v-else
              class="grey--text"
            >
              N/A
            </div>
          </td>
        </tr>
      </template>
      <template slot="no-data">
        <div class="text-center">
          There are no yacht payments.
        </div>
      </template>
    </v-data-table>

    <pagination
      :from="yachtPayments.meta.from"
      :to="yachtPayments.meta.to"
      :total="yachtPayments.meta.total"
      :last-page="yachtPayments.meta.last_page"
      :page.sync="pagination.page"
      class="mt-1 align-center"
    />
  </v-container>
</template>

<script>
import Yacht from '@/models/Yacht'
import StatCard from '@/components/business/logistics/StatCard'
import YachtPayment from '@/models/YachtPayment'
import paginationMixin from '@/mixins/paginationMixin'

export default {
    name: 'YachtsCashFlowIndex',
    components: {
      StatCard
    },
    mixins: [paginationMixin],
    data() {
      return {
        yachtPayments: {
          loading: false,
          data: [],
          meta: {}
        },
        filters: {
          direction: 'inbound',
          year: [this.$moment().year()],
          status: null,
          type: null,
          category: null,
          sold_only: true
        },
        sorting: {
          sortBy: 'date',
          desc: false
        },
        pagination: {
          perPage: 50,
          page: 1
        },
        stats: {
          loading: false,
          data: {}
        },
        years: [
          this.$moment().subtract(2, 'year').year(),
          this.$moment().subtract(1, 'year').year(),
          this.$moment().year(),
          this.$moment().add(1, 'year').year(),
          this.$moment().add(2, 'year').year(),
          this.$moment().add(3, 'year').year()
        ],
        exportingToXlsx: false
      }
    },
    computed: {
      status: function () {
        if (!this.filters.status) return undefined

        return this.filters.status
      },
      headers: function () {
        return [
          { text: 'Yacht ID', value: 'id', sortable: true },
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Client', value: 'client', sortable: true },
          { text: 'Date', value: 'date', sortable: true },
          { text: 'Status', value: 'status', sortable: true },
          { text: 'Amount', value: 'amount', sortable: true },
          { text: 'Type', value: 'type', sortable: true },
          { text: 'Category', value: 'category', sortable: true },
          { text: 'Hull', value: 'hull_number', sortable: true },
          { text: 'Delivery Year', value: 'delivery_year', sortable: true },
          { text: 'Ex Factory', value: 'ex_factory_date', sortable: true },
          { text: 'Investment Program', value: 'investment_program', sortable: true }
        ]
      }
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          if (this.pagination.page !== 1) {
            this.pagination.page = 1
          } else {
            this.fetchYachtPayments()
          }
        }
      }
    },
    created() {
      this.yachtModelSpecs = Yacht
      this.categoryData = YachtPayment.categoryData
      this.statusData = YachtPayment.statusData

      this.fetchYachtPayments()
    },
    methods: {
      async fetchYachtPayments() {
        this.yachtPayments.loading = true
        this.stats.loading = true

        try {
          let queryParams = {
            ...this.paginationQueryParams,
            ...this.filters
          }

          const result = await YachtPayment.api.fetch(queryParams)

          this.yachtPayments.data = result.data
          this.yachtPayments.meta = result.meta

          queryParams = {
            ...this.filters
          }

          this.stats.data = await YachtPayment.api.getStats(queryParams)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtPayments.loading = false
          this.stats.loading = false
        }
      },
      onPaginationUpdate() {
        this.fetchYachtPayments()
      },
      toggleFilter(filter, value) {
        this.filters[filter] = (this.filters[filter] === value) ? null : value
      },
      isYearSelected(year) {
        return this.filters.year.includes(year)
      },
      toggleYearFilter(year) {
        if (this.isYearSelected(year)) {
          // Remove the year if it's already selected
          this.filters.year = this.filters.year.filter(y => y !== year)
          // Ensure at least one year is selected
          if (this.filters.year.length === 0) {
            this.filters.year = [this.$moment().year()]
          }
        } else {
          // Add the year if it's not selected
          this.filters.year = [...this.filters.year, year]
        }
      },
      async downloadXls() {
        let queryParams = {
          ...this.filters
        }

        this.exportingToXlsx = true

        try {
          let result = await YachtPayment.api.downloadXls(queryParams)

          window.open(result.path, '_blank')
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.exportingToXlsx = false
        }
      }

    }
  }
</script>

<style lang="scss">
  .filtered {
    opacity: 0.4;

    &.selected {
      opacity: 1;
    }
  }
</style>
